/* ================================================================
   FITRENO OTPMGER — Gallery, Lightbox, Accordions, Bundle Tiers
   ================================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------------
     Gallery — scroll-snap track + thumbnails + arrows + counter
     ---------------------------------------------------------------- */

  class FroGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.fro-gallery__track');
      if (!this.track) return;

      this.slides = [...this.track.querySelectorAll('.fro-gallery__slide')];
      this.thumbs = [...root.querySelectorAll('.fro-gallery__thumb')];
      this.counter = root.querySelector('.fro-gallery__counter');
      this.prevBtn = root.querySelector('.fro-gallery__arrow--prev');
      this.nextBtn = root.querySelector('.fro-gallery__arrow--next');
      this.current = 0;
      this.total = this.slides.length;

      if (this.total < 2) {
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
      }

      this._observeSlides();
      this._bindArrows();
      this._bindThumbs();
      this._bindKeys();
      this._updateUI();
    }

    _observeSlides() {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting && e.intersectionRatio > 0.5) {
              const idx = this.slides.indexOf(e.target);
              if (idx !== -1 && idx !== this.current) {
                this.current = idx;
                this._updateUI();
              }
            }
          }
        },
        { root: this.track, threshold: 0.51 }
      );
      for (const s of this.slides) io.observe(s);
    }

    _bindArrows() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.current - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.current + 1));
    }

    _bindThumbs() {
      this.thumbs.forEach((btn, i) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.go(i);
        });
      });
    }

    _bindKeys() {
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.current + 1); }
      });
    }

    go(idx) {
      idx = Math.max(0, Math.min(this.total - 1, idx));
      if (idx === this.current) return;
      this.slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.current = idx;
      this._updateUI();
    }

    _updateUI() {
      if (this.counter) this.counter.textContent = `${this.current + 1} / ${this.total}`;
      if (this.prevBtn) this.prevBtn.disabled = this.current === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.current === this.total - 1;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === this.current ? 'true' : 'false'));
    }
  }

  /* ----------------------------------------------------------------
     Lightbox — native <dialog>
     ---------------------------------------------------------------- */

  class FroLightbox {
    constructor(root) {
      this.dialog = root.querySelector('.fro-zoom__dialog');
      if (!this.dialog) return;

      this.track = this.dialog.querySelector('.fro-zoom__track');
      this.slides = [...this.track.querySelectorAll('.fro-zoom__slide')];
      this.thumbs = [...this.dialog.querySelectorAll('.fro-zoom__thumb')];
      this.counter = this.dialog.querySelector('.fro-zoom__counter');
      this.closeBtn = this.dialog.querySelector('.fro-zoom__close');
      this.prevBtn = this.dialog.querySelector('.fro-zoom__arrow--prev');
      this.nextBtn = this.dialog.querySelector('.fro-zoom__arrow--next');
      this.current = 0;
      this.total = this.slides.length;

      this._bindOpen(root);
      this._bindClose();
      this._bindNav();
      this._observeSlides();
    }

    _bindOpen(root) {
      root.querySelectorAll('.fro-gallery__link').forEach((link, i) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(i);
        });
      });
    }

    _bindClose() {
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    _bindNav() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.current - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.current + 1));

      this.thumbs.forEach((btn, i) => {
        btn.addEventListener('click', () => this.go(i));
      });

      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.current + 1); }
      });
    }

    _observeSlides() {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting && e.intersectionRatio > 0.5) {
              const idx = this.slides.indexOf(e.target);
              if (idx !== -1) {
                this.current = idx;
                this._updateUI();
              }
            }
          }
        },
        { root: this.track, threshold: 0.51 }
      );
      for (const s of this.slides) io.observe(s);
    }

    open(idx) {
      this.current = idx;
      this._lazyLoadAll();
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this.slides[idx].scrollIntoView({ block: 'nearest', inline: 'center' });
        this._updateUI();
      });
    }

    close() {
      this.dialog.close();
      document.documentElement.style.overflow = '';
    }

    go(idx) {
      idx = Math.max(0, Math.min(this.total - 1, idx));
      this.slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.current = idx;
      this._updateUI();
    }

    _lazyLoadAll() {
      this.dialog.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }

    _updateUI() {
      if (this.counter) this.counter.textContent = `${this.current + 1} / ${this.total}`;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === this.current ? 'true' : 'false'));
    }
  }

  /* ----------------------------------------------------------------
     Accordions — hash-based open + smooth toggle
     ---------------------------------------------------------------- */

  function initAccordions() {
    var hash = window.location.hash;
    if (hash) {
      var target = document.querySelector(hash);
      if (target) {
        var acc = target.closest('details.fro-acc');
        if (acc) {
          acc.open = true;
          requestAnimationFrame(function () {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      }
    }

    window.addEventListener('hashchange', function () {
      var h = window.location.hash;
      if (!h) return;
      var el = document.querySelector(h);
      if (!el) return;
      var a = el.closest('details.fro-acc');
      if (a) {
        a.open = true;
        requestAnimationFrame(function () {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    });
  }

  /* ----------------------------------------------------------------
     Bundle tiers — radio ↔ quantity stepper sync
     ---------------------------------------------------------------- */

  function initBundleTiers() {
    document.querySelectorAll('.fro-bundle').forEach(function (fieldset) {
      var radios = fieldset.querySelectorAll('.fro-bundle__input');
      if (!radios.length) return;

      var section = fieldset.closest('[data-section-id]') || fieldset.closest('section');
      if (!section) return;

      var stepper = section.querySelector('quantity-input, .quantity-input, [data-quantity-input]');
      var qtyInput = stepper
        ? stepper.querySelector('input[type="number"], input[name="quantity"]')
        : null;

      function setQty(n) {
        if (!qtyInput) return;
        qtyInput.value = n;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      function getCheckedQty() {
        var checked = fieldset.querySelector('.fro-bundle__input:checked');
        return checked ? parseInt(checked.value, 10) || 1 : 1;
      }

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (this.checked) setQty(parseInt(this.value, 10) || 1);
        });
      });

      if (qtyInput) {
        var observer = new MutationObserver(function () {
          syncFromStepper();
        });
        observer.observe(qtyInput, { attributes: true, attributeFilter: ['value'] });

        qtyInput.addEventListener('change', syncFromStepper);
        qtyInput.addEventListener('input', syncFromStepper);

        function syncFromStepper() {
          var val = parseInt(qtyInput.value, 10);
          if (!val || val < 1) return;
          var best = null;
          radios.forEach(function (r) {
            var rv = parseInt(r.value, 10);
            if (rv <= val && (!best || rv > parseInt(best.value, 10))) best = r;
          });
          if (best && !best.checked) {
            best.checked = true;
          }
        }
      }

      if (!fieldset.querySelector('.fro-bundle__input:checked') && radios[0]) {
        radios[0].checked = true;
        setQty(parseInt(radios[0].value, 10) || 1);
      }
    });
  }

  /* ----------------------------------------------------------------
     Sticky add-to-cart — sync thumbnail from gallery
     ---------------------------------------------------------------- */

  function initStickyCartSync() {
    var sticky = document.querySelector('sticky-add-to-cart');
    if (!sticky) return;

    var galleryRoot = document.querySelector('.fro-gallery');
    if (!galleryRoot) return;

    var stickyImg = sticky.querySelector('img');
    if (!stickyImg) return;

    var observer = new MutationObserver(function () {
      var current = galleryRoot.querySelector('.fro-gallery__thumb[aria-current="true"] img');
      if (current && current.src) {
        stickyImg.src = current.src;
        stickyImg.alt = current.alt || '';
      }
    });

    galleryRoot.querySelectorAll('.fro-gallery__thumb').forEach(function (t) {
      observer.observe(t, { attributes: true, attributeFilter: ['aria-current'] });
    });
  }

  /* ----------------------------------------------------------------
     Boot
     ---------------------------------------------------------------- */

  function init() {
    document.querySelectorAll('.fro-gallery').forEach(function (el) {
      new FroGallery(el);
      new FroLightbox(el.closest('.fro-gallery-wrap') || el.parentElement);
    });

    initAccordions();
    initBundleTiers();
    initStickyCartSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
